import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UseFavoritesOptions {
  onAuthRequired?: () => void;
}

export const useFavorites = (userId: string | undefined, options?: UseFavoritesOptions) => {
  const [favorites, setFavorites] = useState<{ eventIds: string[]; experienceIds: string[] }>({
    eventIds: [],
    experienceIds: [],
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (userId) {
      fetchFavorites();
    } else {
      setFavorites({ eventIds: [], experienceIds: [] });
      setLoading(false);
    }
  }, [userId]);

  const fetchFavorites = async () => {
    if (!userId) return;
    
    const { data, error } = await supabase
      .from('favorites')
      .select('event_id, experience_id')
      .eq('user_id', userId);

    if (!error && data) {
      setFavorites({
        eventIds: data.filter(f => f.event_id).map(f => f.event_id!),
        experienceIds: data.filter(f => f.experience_id).map(f => f.experience_id!),
      });
    }
    setLoading(false);
  };

  const toggleEventFavorite = useCallback(async (eventId: string) => {
    if (!userId) {
      if (options?.onAuthRequired) {
        options.onAuthRequired();
      } else {
        toast({ title: 'Please sign in to save favorites', variant: 'destructive' });
      }
      return;
    }

    const isFavorite = favorites.eventIds.includes(eventId);

    if (isFavorite) {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', userId)
        .eq('event_id', eventId);

      if (!error) {
        setFavorites(prev => ({
          ...prev,
          eventIds: prev.eventIds.filter(id => id !== eventId),
        }));
        toast({ title: 'Removed from favorites' });
      }
    } else {
      const { error } = await supabase
        .from('favorites')
        .insert({ user_id: userId, event_id: eventId });

      if (!error) {
        setFavorites(prev => ({
          ...prev,
          eventIds: [...prev.eventIds, eventId],
        }));
        toast({ title: 'Added to favorites' });
      }
    }
  }, [userId, options, toast]);

  const toggleExperienceFavorite = useCallback(async (experienceId: string) => {
    if (!userId) {
      if (options?.onAuthRequired) {
        options.onAuthRequired();
      } else {
        toast({ title: 'Please sign in to save favorites', variant: 'destructive' });
      }
      return;
    }

    const isFavorite = favorites.experienceIds.includes(experienceId);

    if (isFavorite) {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', userId)
        .eq('experience_id', experienceId);

      if (!error) {
        setFavorites(prev => ({
          ...prev,
          experienceIds: prev.experienceIds.filter(id => id !== experienceId),
        }));
        toast({ title: 'Removed from favorites' });
      }
    } else {
      const { error } = await supabase
        .from('favorites')
        .insert({ user_id: userId, experience_id: experienceId });

      if (!error) {
        setFavorites(prev => ({
          ...prev,
          experienceIds: [...prev.experienceIds, experienceId],
        }));
        toast({ title: 'Added to favorites' });
      }
    }
  }, [userId, options, toast]);

  const isEventFavorite = useCallback((eventId: string) => favorites.eventIds.includes(eventId), [favorites.eventIds]);
  const isExperienceFavorite = useCallback((experienceId: string) => favorites.experienceIds.includes(experienceId), [favorites.experienceIds]);

  return {
    favorites,
    loading,
    toggleEventFavorite,
    toggleExperienceFavorite,
    isEventFavorite,
    isExperienceFavorite,
    refetch: fetchFavorites,
  };
};
